import { useState, useEffect, useRef } from "react";
import EmpLayout from "./emp_layout";
import './css/business_profile.css';
import { API_ENDPOINTS } from "../../api/config";

export function BusinessProfile() {
  const [editing, setEditing] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null); // Reference hook pointer targeting hidden file input

  const [counties, setCounties] = useState([]);       
  const [businessTypes, setBusinessTypes] = useState([]); 

  const [form, setForm] = useState({
    name: "", type: "", city: "", address: "",
    phone: "", email: "", website: "", bio: "",
    seats: "", founded: "", location: ""
  });

  // Isolated method to safely retrieve protected logo byte stream
  const fetchSecureLogo = async (headers) => {
    try {
      const logoRes = await fetch(API_ENDPOINTS.EMPLOYERPROFILELOGO, { headers });
      if (logoRes.ok) {
        const imageBlob = await logoRes.blob();
        const localImageObjectUrl = URL.createObjectURL(imageBlob);
        setLogoUrl(localImageObjectUrl);
      }
    } catch (err) {
      console.error("Error retrieving logo stream:", err);
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const token = localStorage.getItem("token"); 
        const headers = { 'Authorization': `Bearer ${token}` };

        const [profileRes, countiesRes, typesRes] = await Promise.all([
          fetch(API_ENDPOINTS.EMPLOYERPROFILE, { headers }),
          fetch(`${API_ENDPOINTS.DIMENSIONS}/counties`), 
          fetch(`${API_ENDPOINTS.DIMENSIONS}/business-types`) 
        ]);

        if (countiesRes.ok) {
          const countiesData = await countiesRes.json();
          setCounties(countiesData);
        }
        
        if (typesRes.ok) {
          const typesData = await typesRes.json();
          setBusinessTypes(typesData); 
        }

        if (profileRes.ok) {
          const data = await profileRes.json();
          setForm({
            name: data.businessName || "",
            type: data.businessType || "",
            city: data.city || "", 
            address: data.address || "",
            phone: data.phone || "",
            email: data.email || "",
            website: data.website || "",
            bio: data.bio || "",
            seats: data.noOfSeats?.toString() || "",
            founded: data.yearFounded?.toString() || "",
            location: data.location || ""
          });
        }

        // Fetch secure logo stream logic integration
        await fetchSecureLogo(headers);

      } catch (error) {
        console.error("Error setting up profile lookups:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();

    return () => {
      if (logoUrl) URL.revokeObjectURL(logoUrl);
    };
  }, []);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  // Form submission mutation method pipeline implementation
  const saveChanges = async (section) => {
    try {
      const token = localStorage.getItem("token");
      
      // Convert state text values back to strict API data shapes matching incoming parameters
      const payload = {
        businessName: form.name,
        bio: form.bio,
        businessType: form.type,
        yearFounded: form.founded ? parseInt(form.founded, 10) : null,
        noOfSeats: form.seats ? parseInt(form.seats, 10) : null,
        phone: form.phone,
        email: form.email,
        website: form.website,
        address: form.address,
        city: form.city
      };

      const response = await fetch(API_ENDPOINTS.EMPLOYERPROFILE, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        alert("Profile changes stored successfully!");
        setEditing(null);
      } else {
        const errorData = await response.json();
        alert(errorData.message || "Failed updating business profile information.");
      }
    } catch (err) {
      console.error("Error submitting profile state changes:", err);
    }
  };

  // Multipart form transmission handling image binary transformations
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${API_ENDPOINTS.EMPLOYERPROFILELOGO}-upload`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`
        },
        body: formData
      });

      if (response.ok) {
        // Refresh local URL stream blob data representation immediately on success
        const headers = { 'Authorization': `Bearer ${token}` };
        await fetchSecureLogo(headers);
        alert("Business logo updated successfully!");
      } else {
        alert("Failed uploading chosen logo asset layout.");
      }
    } catch (err) {
      console.error("Network upload error encountered:", err);
    }
  };

  if (loading) return <div>Loading Profile Settings...</div>;

  const initials = form.name ? form.name.split(" ").map(n => n[0]).join("").substring(0, 2).toUpperCase() : "BIZ";

  return (
    <EmpLayout title="Business" titleEm="Profile">
      <div className="bp-layout">
        <div>
          <div className="bp-logo-card">
            <div className="bp-big-logo">
              {logoUrl ? (
                <img src={logoUrl} alt="Business Logo" style={{ width: '100%', height: '100%', borderRadius: 'inherit', objectFit: 'cover' }} />
              ) : (
                initials
              )}
            </div>
            <div className="bp-biz-name">{form.name || "Business Name"}</div>
            <div className="bp-biz-type">{form.type || "Type"} · {form.city || "City"}</div>
            
            
            <button className="bp-change-logo" onClick={() => fileInputRef.current.click()}>📷 Update logo</button>
            <input 
              type="file" 
              ref={fileInputRef} 
              style={{ display: "none" }} 
              accept="image/*" 
              onChange={handleLogoUpload} 
            />
            
          </div>
        </div>

        <div>
          {/* About Section */}
          <div className="bp-section-card">
            <div className="bp-section-hdr">
              <div className="bp-section-title">About <em>your business</em></div>
              <button className={`bp-edit-btn${editing === "about" ? " active" : ""}`} onClick={() => setEditing(editing === "about" ? null : "about")}>{editing === "about" ? "Cancel" : "Edit"}</button>
            </div>
            {editing === "about" ? (
              <>
                <div className="emp-field"><label className="emp-label">About / Description</label><textarea className="emp-textarea" rows={5} value={form.bio} onChange={set("bio")} /></div>
                <div className="emp-grid2">
                  <div className="emp-field"><label className="emp-label">Business Name</label><input className="emp-input" value={form.name} onChange={set("name")} /></div>
                  
                  <div className="emp-field">
                    <label className="emp-label">Business Type</label>
                    <select className="emp-select" value={form.type} onChange={set("type")}>
                      <option value="">Select Business Type</option>
                      {businessTypes.map(typeString => (
                        <option key={typeString} value={typeString}>{typeString}</option>
                      ))}
                    </select>
                  </div>

                  <div className="emp-field"><label className="emp-label">Number of Seats</label><input className="emp-input" type="number" value={form.seats} onChange={set("seats")} /></div>
                  <div className="emp-field"><label className="emp-label">Year Founded</label><input className="emp-input" type="number" value={form.founded} onChange={set("founded")} /></div>
                  
                  <div className="emp-field">
                    <label className="emp-label">County / Location</label>
                    <select className="emp-select" value={form.city} onChange={set("city")}>
                      <option value="">Select County</option>
                      {counties.map(countyObj => (
                        <option key={countyObj.id} value={countyObj.name}>{countyObj.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="bp-save-row">
                  <button className="emp-btn-primary" onClick={() => saveChanges("about")}>Save changes</button>
                  <button className="emp-btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </>
            ) : (
              <>
                <div className="emp-field" style={{ marginBottom: "1rem" }}><span className="bp-field-label">About</span><span className="bp-field-value">{form.bio || "No description provided."}</span></div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                  {[["Business Type", form.type], ["Seats", form.seats], ["Founded", form.founded], ["Location", form.city]].map(([k, v]) => (
                    <div key={k}><span className="bp-field-label">{k}</span><span className="bp-field-value">{v || "—"}</span></div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Contact Section */}
          <div className="bp-section-card">
            <div className="bp-section-hdr">
              <div className="bp-section-title">Contact <em>details</em></div>
              <button className={`bp-edit-btn${editing === "contact" ? " active" : ""}`} onClick={() => setEditing(editing === "contact" ? null : "contact")}>{editing === "contact" ? "Cancel" : "Edit"}</button>
            </div>
            {editing === "contact" ? (
              <>
                <div className="emp-grid2">
                  <div className="emp-field"><label className="emp-label">Phone</label><input className="emp-input" value={form.phone} onChange={set("phone")} /></div>
                  <div className="emp-field"><label className="emp-label">Email</label><input className="emp-input" type="email" value={form.email} onChange={set("email")} /></div>
                  <div className="emp-field"><label className="emp-label">Website</label><input className="emp-input" value={form.website} onChange={set("website")} /></div>
                  <div className="emp-field"><label className="emp-label">Address</label><input className="emp-input" value={form.address} onChange={set("address")} /></div>
                </div>
                <div className="bp-save-row">
                  <button className="emp-btn-primary" onClick={() => saveChanges("contact")}>Save changes</button>
                  <button className="emp-btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
                </div>
              </>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                {[["Phone", form.phone], ["Email", form.email], ["Website", form.website], ["Address", form.address]].map(([k, v]) => (
                  <div key={k}><span className="bp-field-label">{k}</span><span className="bp-field-value">{v || "—"}</span></div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </EmpLayout>
  );
}